// src/contexts/CaseContext.tsx
// BRIDGE FIX: frontend uses agency_id/client_id (snake_case)
// DB has: tenantId/clientId (camelCase Prisma)
// Interface IDENTICAL to original — zero page changes needed

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAgency } from './AgencyContext';
import { useClient } from './ClientContext';

// ─── Types (unchanged from original) ─────────────────────────────────────────

export interface Case {
  id: string;
  client_id: string;
  agency_id: string;
  country: string;
  visa_type: string;
  status: 'draft' | 'assessment' | 'document_collection' | 'submitted' | 'approved' | 'rejected';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  assigned_to?: string;
  created_at: string;
  updated_at: string;
  closed_at?: string;
  metadata?: Record<string, any>;
  // Extra fields from DB
  ref_code?: string;
  notes?: string;
  fee_quoted?: number;
}

interface CaseContextType {
  cases: Case[];
  currentCase: Case | null;
  loading: boolean;
  error: string | null;
  fetchCasesForClient: (clientId: string) => Promise<void>;
  fetchAllCases: () => Promise<void>;
  fetchCaseById: (id: string) => Promise<Case | null>;
  createCase: (data: Partial<Case>) => Promise<Case | null>;
  updateCase: (id: string, data: Partial<Case>) => Promise<Case | null>;
  deleteCase: (id: string) => Promise<boolean>;
  setCurrentCase: (c: Case | null) => void;
  refreshCases: () => void;
}

const CaseContext = createContext<CaseContextType | undefined>(undefined);

// ─── Helper: map DB row (camelCase) → Case (snake_case) ──────────────────────
function mapDbToCase(row: any): Case {
  // DB status values are uppercase (ASSESSMENT, SUBMITTED etc)
  // Frontend expects lowercase
  const statusMap: Record<string, string> = {
    DRAFT: 'draft', ASSESSMENT: 'assessment',
    DOCUMENT_COLLECTION: 'document_collection',
    SUBMITTED: 'submitted', APPROVED: 'approved',
    REFUSED: 'rejected', REJECTED: 'rejected', WITHDRAWN: 'draft',
  };
  const priorityMap: Record<string, string> = {
    LOW: 'low', MEDIUM: 'medium', HIGH: 'high', URGENT: 'urgent',
  };

  return {
    id: row.id,
    client_id: row.clientId ?? row.client_id ?? '',
    agency_id: row.tenantId ?? row.agency_id ?? '',
    country: row.country ?? 'UK',
    visa_type: row.visaType ?? row.visa_type ?? '',
    status: (statusMap[row.status] ?? row.status ?? 'assessment') as Case['status'],
    priority: (priorityMap[row.priority] ?? row.priority ?? 'medium') as Case['priority'],
    assigned_to: row.assignedTo ?? row.assigned_to,
    created_at: row.createdAt ?? row.created_at ?? new Date().toISOString(),
    updated_at: row.updatedAt ?? row.updated_at ?? new Date().toISOString(),
    closed_at: row.decisionDate ?? row.closed_at,
    metadata: row.metadata ?? {},
    ref_code: row.refCode ?? row.ref_code,
    notes: row.notes,
    fee_quoted: row.feeQuoted ?? row.fee_quoted,
  };
}

// ─── Helper: map Case (snake_case) → DB insert (camelCase) ───────────────────
function mapCaseToDb(data: Partial<Case>, tenantId: string): Record<string, any> {
  const statusMap: Record<string, string> = {
    draft: 'ASSESSMENT', assessment: 'ASSESSMENT',
    document_collection: 'DOCUMENT_COLLECTION',
    submitted: 'SUBMITTED', approved: 'APPROVED', rejected: 'REFUSED',
  };
  const priorityMap: Record<string, string> = {
    low: 'LOW', medium: 'MEDIUM', high: 'HIGH', urgent: 'URGENT',
  };

  const result: Record<string, any> = {
    tenantId,
  };

  if (data.client_id !== undefined) result.clientId = data.client_id;
  if (data.country !== undefined) result.country = data.country;
  if (data.visa_type !== undefined) result.visaType = data.visa_type;
  if (data.status !== undefined) result.status = statusMap[data.status] ?? data.status.toUpperCase();
  if (data.priority !== undefined) result.priority = priorityMap[data.priority] ?? data.priority.toUpperCase();
  if (data.assigned_to !== undefined) result.assignedTo = data.assigned_to;
  if (data.notes !== undefined) result.notes = data.notes;
  if (data.fee_quoted !== undefined) result.feeQuoted = data.fee_quoted;
  if (data.metadata !== undefined) result.metadata = data.metadata;

  return result;
}

// ─── Provider ────────────────────────────────────────────────────────────────

export const CaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentAgency } = useAgency();
  const { currentClient } = useClient();
  const [cases, setCases] = useState<Case[]>([]);
  const [currentCase, setCurrentCase] = useState<Case | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Fetch cases for a client ──────────────────────────────────────────────
  const fetchCasesForClient = useCallback(async (clientId: string) => {
    if (!currentAgency) { setCases([]); return; }
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase
        
        .from('agency_cases')
        .select('*')
        .eq('clientId', clientId)       // camelCase in DB
        .eq('tenantId', currentAgency.id) // camelCase in DB
        .order('createdAt', { ascending: false });
      if (e) throw e;
      setCases((data ?? []).map(mapDbToCase));
    } catch (err: any) {
      console.error('fetchCasesForClient error:', err?.message ?? err);
      setError('Unable to load cases. Please try again.'); setCases([]);
    } finally { setLoading(false); }
  }, [currentAgency]);

  // ── Fetch ALL cases for agency (used by pipeline/dashboard) ────────────────
  const fetchAllCases = useCallback(async () => {
    if (!currentAgency) { setCases([]); return; }
    setLoading(true); setError(null);
    try {
      const { data, error: e } = await supabase
        .from('agency_cases')
        .select('*')
        .eq('tenantId', currentAgency.id)
        .order('createdAt', { ascending: false });
      if (e) throw e;
      setCases((data ?? []).map(mapDbToCase));
    } catch (err: any) {
      console.error('fetchAllCases error:', err?.message ?? err);
      setError('Unable to load cases. Please try again.'); setCases([]);
    } finally { setLoading(false); }
  }, [currentAgency]);

  // Auto-load all cases when agency is ready
  useEffect(() => {
    if (currentAgency) fetchAllCases();
  }, [currentAgency, fetchAllCases]);

  // ── Fetch single case ─────────────────────────────────────────────────────
  const fetchCaseById = useCallback(async (id: string): Promise<Case | null> => {
    if (!currentAgency) return null;
    try {
      const { data, error: e } = await supabase
        
        .from('agency_cases')
        .select('*')
        .eq('id', id)
        .eq('tenantId', currentAgency.id)
        .single();
      if (e) throw e;
      return mapDbToCase(data);
    } catch (err: any) {
      console.error('fetchCaseById error:', err.message);
      return null;
    }
  }, [currentAgency]);

  // ── Create case ───────────────────────────────────────────────────────────
  const createCase = useCallback(async (data: Partial<Case>): Promise<Case | null> => {
    if (!currentAgency) { setError('No agency selected'); return null; }
    if (!data.client_id) { setError('Client ID required'); return null; }
    try {
      const now = new Date().toISOString();
      const refCode = `REF-${Date.now().toString().slice(-6)}-${Math.random().toString(36).substring(2, 5).toUpperCase()}`;
      const dbRow = {
        ...mapCaseToDb(data, currentAgency.id),
        id: crypto.randomUUID().replace(/-/g, '').substring(0, 24),
        refCode,
        createdAt: now,
        updatedAt: now,
      };

      const { data: created, error: e } = await supabase
        
        .from('agency_cases')
        .insert([dbRow])
        .select()
        .single();
      if (e) throw e;

      const mapped = mapDbToCase(created);
      if (currentClient && created.clientId === currentClient.id) {
        setCases(prev => [mapped, ...prev]);
      }
      return mapped;
    } catch (err: any) {
      console.error('createCase error:', err?.message ?? err);
      setError('Unable to create case. Please try again.'); return null;
    }
  }, [currentAgency, currentClient]);

  // ── Update case ───────────────────────────────────────────────────────────
  const updateCase = useCallback(async (id: string, data: Partial<Case>): Promise<Case | null> => {
    if (!currentAgency) return null;
    try {
      const dbUpdates = {
        ...mapCaseToDb(data, currentAgency.id),
        updatedAt: new Date().toISOString(),
      };
      // Remove tenantId from updates (can't change tenant)
      delete dbUpdates.tenantId;

      const { data: updated, error: e } = await supabase
        
        .from('agency_cases')
        .update(dbUpdates)
        .eq('id', id)
        .eq('tenantId', currentAgency.id)
        .select()
        .single();
      if (e) throw e;

      const mapped = mapDbToCase(updated);
      setCases(prev => prev.map(c => c.id === id ? mapped : c));
      if (currentCase?.id === id) setCurrentCase(mapped);
      return mapped;
    } catch (err: any) {
      console.error('updateCase error:', err?.message ?? err);
      setError('Unable to update case. Please try again.'); return null;
    }
  }, [currentAgency, currentCase]);

  // ── Delete case (soft withdraw) ───────────────────────────────────────────
  const deleteCase = useCallback(async (id: string): Promise<boolean> => {
    if (!currentAgency) return false;
    try {
      const { error: e } = await supabase
        
        .from('agency_cases')
        .update({ status: 'WITHDRAWN', updatedAt: new Date().toISOString() })
        .eq('id', id)
        .eq('tenantId', currentAgency.id);
      if (e) throw e;
      setCases(prev => prev.filter(c => c.id !== id));
      if (currentCase?.id === id) setCurrentCase(null);
      return true;
    } catch { return false; }
  }, [currentAgency, currentCase]);

  const refreshCases = useCallback(() => {
    if (currentClient) fetchCasesForClient(currentClient.id);
  }, [currentClient, fetchCasesForClient]);

  useEffect(() => {
    if (currentClient) fetchCasesForClient(currentClient.id);
    else { setCases([]); setCurrentCase(null); }
  }, [currentClient, fetchCasesForClient]);

  return (
    <CaseContext.Provider value={{
      cases, currentCase, loading, error,
      fetchCasesForClient, fetchCaseById, fetchAllCases,
      createCase, updateCase, deleteCase,
      setCurrentCase, refreshCases,
    }}>
      {children}
    </CaseContext.Provider>
  );
};

export const useCase = () => {
  const ctx = useContext(CaseContext);
  if (!ctx) throw new Error('useCase must be used within CaseProvider');
  return ctx;
};
